import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Icons removed - text-only UI

// ============================================================================
// COLOR PRESETS
// ============================================================================

const PRIMARY_COLORS = [
  { id: "navy", hex: "#1e3a5f", name: "Navy" },
  { id: "blue", hex: "#2563eb", name: "Blue" },
  { id: "sky", hex: "#0ea5e9", name: "Sky" },
  { id: "teal", hex: "#0d9488", name: "Teal" },
  { id: "green", hex: "#16a34a", name: "Green" },
  { id: "red", hex: "#dc2626", name: "Red" },
  { id: "orange", hex: "#ea580c", name: "Orange" },
  { id: "amber", hex: "#d97706", name: "Amber" },
  { id: "purple", hex: "#7c3aed", name: "Purple" },
  { id: "gray", hex: "#4b5563", name: "Gray" },
  { id: "silver", hex: "#9ca3af", name: "Silver" },
  { id: "midnight", hex: "#1e293b", name: "Midnight" },
];

const SECONDARY_COLORS = [
  { id: "white", hex: "#ffffff", name: "White" },
  { id: "cream", hex: "#fef3c7", name: "Cream" },
  { id: "lightgray", hex: "#e5e7eb", name: "Light Gray" },
  { id: "gold", hex: "#fcd34d", name: "Gold" },
  { id: "silver", hex: "#d1d5db", name: "Silver" },
  { id: "ice", hex: "#e0f2fe", name: "Ice Blue" },
];

const ACCENT_COLORS = [
  { id: "gold", hex: "#fbbf24", name: "Gold" },
  { id: "red", hex: "#ef4444", name: "Red" },
  { id: "orange", hex: "#f97316", name: "Orange" },
  { id: "cyan", hex: "#06b6d4", name: "Cyan" },
  { id: "lime", hex: "#84cc16", name: "Lime" },
  { id: "pink", hex: "#ec4899", name: "Pink" },
  { id: "white", hex: "#ffffff", name: "White" },
  { id: "silver", hex: "#9ca3af", name: "Silver" },
];

const LIVERY_PATTERNS = [
  { id: "solid", name: "Solid" },
  { id: "cheatline", name: "Cheatline" },
  { id: "eurowhite", name: "Eurowhite" },
  { id: "twotone", name: "Two-Tone" },
  { id: "retro", name: "Retro" },
  { id: "swoosh", name: "Swoosh" },
];

const TAIL_DESIGNS = [
  { id: "solid", name: "Solid" },
  { id: "stripe", name: "Stripe" },
  { id: "gradient", name: "Gradient" },
  { id: "split", name: "Split" },
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 255, g: 255, b: 255 };
}

function getBrightness(hex) {
  const rgb = hexToRgb(hex);
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 / 255;
}

function blendColors(color1, color2, factor) {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * factor),
    g: Math.round(color1.g + (color2.g - color1.g) * factor),
    b: Math.round(color1.b + (color2.b - color1.b) * factor),
  };
}

// ============================================================================
// LIVERY DESIGNER COMPONENT
// ============================================================================

export default function LiveryDesigner({ onComplete, initialValues = {} }) {
  const navigate = useNavigate();
  const { worldState, updateWorldState } = useWorldState();
  
  // Use worldState values as defaults
  const defaults = {
    name: worldState?.airline?.name || "Pacific Wings",
    code: worldState?.airline?.code || "PW",
    callsign: worldState?.airline?.callsign || "PACWING",
    primaryColor: worldState?.airline?.primaryColor || "#1e3a5f",
    secondaryColor: worldState?.airline?.secondaryColor || "#ffffff",
    accentColor: worldState?.airline?.accentColor || "#fbbf24",
    pattern: worldState?.airline?.pattern || "eurowhite",
    tailDesign: worldState?.airline?.tailDesign || "solid",
    ...initialValues
  };

  // Form state
  const [airlineName, setAirlineName] = useState(defaults.name);
  const [airlineCode, setAirlineCode] = useState(defaults.code);
  const [callsign, setCallsign] = useState(defaults.callsign);
  const [primaryColor, setPrimaryColor] = useState(defaults.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(defaults.secondaryColor);
  const [accentColor, setAccentColor] = useState(defaults.accentColor);
  const [pattern, setPattern] = useState(defaults.pattern);
  const [tailDesign, setTailDesign] = useState(defaults.tailDesign);

  // Canvas refs
  const canvasRef = useRef(null);
  const baseImageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load base aircraft image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    
    const paths = [
      "./assets/737-800.png"
    ];
    
    let pathIndex = 0;
    
    const tryLoad = () => {
      if (pathIndex >= paths.length) {
        console.error("Failed to load aircraft image from any path");
        return;
      }
      img.src = paths[pathIndex];
    };
    
    img.onload = () => {
      baseImageRef.current = img;
      setImageLoaded(true);
    };
    
    img.onerror = () => {
      pathIndex++;
      tryLoad();
    };
    
    tryLoad();
  }, []);

  // Render livery to canvas
  const renderLivery = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const img = baseImageRef.current;
    
    if (!canvas || !ctx || !img || !imageLoaded) return;

    const width = canvas.width;
    const height = canvas.height;

    // Clear and draw base
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    // Get image data
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    const primary = hexToRgb(primaryColor);
    const secondary = hexToRgb(secondaryColor);
    const accent = hexToRgb(accentColor);

    // Process ALL pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        // Skip transparent pixels
        if (a < 10) continue;

        const nx = x / width;
        const ny = y / height;
        const brightness = (r + g + b) / 3 / 255;
        
        // Skip very dark areas (windows, landing gear, shadows)
        if (brightness < 0.20) continue;

        // Is this pixel in the tail region?
        const inTail = nx > 0.74 && ny > 0.15 && ny < 0.55;
        
        // Is this a light/white pixel that should be colored?
        const isColorable = brightness > 0.22;
        
        if (!isColorable) continue;

        let targetColor = null;

        // Determine color based on pattern
        if (pattern === "solid") {
          targetColor = primary;
        } 
        else if (pattern === "eurowhite") {
          targetColor = inTail ? primary : secondary;
        }
        else if (pattern === "cheatline") {
          if (inTail) {
            targetColor = primary;
          } else if (ny > 0.42 && ny < 0.48 && nx < 0.75) {
            targetColor = primary;
          } else {
            targetColor = secondary;
          }
        }
        else if (pattern === "twotone") {
          if (inTail) {
            targetColor = primary;
          } else if (ny < 0.48) {
            targetColor = primary;
          } else {
            targetColor = secondary;
          }
        }
        else if (pattern === "retro") {
          if (inTail) {
            targetColor = primary;
          } else if (ny < 0.44 && nx < 0.75) {
            targetColor = primary;
          } else if (ny >= 0.44 && ny < 0.48 && nx < 0.75) {
            targetColor = accent;
          } else {
            targetColor = secondary;
          }
        }
        else if (pattern === "swoosh") {
          if (inTail) {
            targetColor = primary;
          } else {
            const swooshLine = 0.40 + (nx * 0.20);
            if (ny < swooshLine) {
              targetColor = primary;
            } else {
              targetColor = secondary;
            }
          }
        }
        else {
          targetColor = secondary;
        }

        // Apply tail design variations
        if (inTail && targetColor) {
          if (tailDesign === "stripe") {
            if (nx > 0.82 && nx < 0.87) {
              targetColor = accent;
            }
          } else if (tailDesign === "gradient") {
            const tailProgress = (ny - 0.15) / 0.40;
            targetColor = blendColors(accent, primary, tailProgress);
          } else if (tailDesign === "split") {
            const splitLine = 0.15 + (nx - 0.74) * 1.5;
            if (ny < splitLine) {
              targetColor = accent;
            }
          }
        }

        // Apply color with shading preservation
        if (targetColor) {
          const shade = Math.max(0.3, brightness);
          data[i] = Math.min(255, Math.round(targetColor.r * shade * 1.1));
          data[i + 1] = Math.min(255, Math.round(targetColor.g * shade * 1.1));
          data[i + 2] = Math.min(255, Math.round(targetColor.b * shade * 1.1));
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);

    // Draw airline name with outline for visibility
    ctx.font = "bold 32px Arial";
    ctx.textAlign = "left";
    
    const textX = width * 0.08;
    const textY = height * 0.44;
    const text = airlineName.toUpperCase();
    
    // Draw outline/stroke first (dark)
    ctx.strokeStyle = "#000000";
    ctx.lineWidth = 4;
    ctx.strokeText(text, textX, textY);
    
    // Then fill with white (always readable)
    ctx.fillStyle = "#ffffff";
    ctx.fillText(text, textX, textY);

  }, [imageLoaded, primaryColor, secondaryColor, accentColor, pattern, tailDesign, airlineName]);

  useEffect(() => {
    renderLivery();
  }, [renderLivery]);

  const handleComplete = () => {
    const config = {
      name: airlineName,
      code: airlineCode,
      callsign: callsign,
      pattern: pattern,
      tailDesign: tailDesign,
      primaryColor: primaryColor,
      secondaryColor: secondaryColor,
      accentColor: accentColor,
    };

    // Save to localStorage so the game knows we have an airline
    const playerAirline = {
      name: airlineName,
      iataCode: airlineCode,
      callsign: callsign,
      colors: {
        primary: primaryColor,
        secondary: secondaryColor,
        accent: accentColor,
      },
      livery: {
        pattern: pattern,
        tailDesign: tailDesign,
      }
    };
    localStorage.setItem('playerAirline', JSON.stringify(playerAirline));

    updateWorldState(prev => ({
      ...prev,
      airline: { ...prev.airline, ...config }
    }));

    if (onComplete) onComplete(config);
    navigate("/opscenter");
  };

  // Reusable button style
  const btnStyle = (isSelected) => ({
    backgroundColor: isSelected ? "#60ffd0" : "#1a1a1a",
    color: isSelected ? "#000" : "#888",
    border: isSelected ? "2px solid #60ffd0" : "2px solid #333",
    padding: "8px 12px",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: isSelected ? "bold" : "normal",
    transition: "all 0.15s ease",
    userSelect: "none",
  });

  return (
    <div style={{ 
      minHeight: "100vh", 
      padding: "16px", 
      backgroundColor: "#0a0a0a", 
      color: "#e5e5e5" 
    }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "16px" }}>
          <h1 style={{ 
            fontSize: "28px", 
            fontWeight: "bold", 
            marginBottom: "4px", 
            color: "#60ffd0" 
          }}>
            CREATE YOUR AIRLINE
          </h1>
          <p style={{ color: "#737373", fontSize: "14px" }}>
            Design your identity before taking to the skies
          </p>
        </div>

        {/* BIG PREVIEW - Full Width */}
        <div style={{ 
          backgroundColor: "#050505", 
          borderRadius: "8px", 
          padding: "8px", 
          marginBottom: "16px",
          border: "1px solid #1a1a1a"
        }}>
          <canvas
            ref={canvasRef}
            width={1536}
            height={1024}
            style={{ 
              width: "100%",
              maxWidth: "100%",
              height: "auto",
              display: "block",
              margin: "0 auto"
            }}
          />
          {!imageLoaded && (
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "300px"
            }}>
              <div style={{ textAlign: "center" }}>
                <span style={{
                  fontSize: "1.5rem",
                  color: "#60ffd0",
                  fontWeight: "bold"
                }}>LOADING</span>
                <p style={{ color: "#737373", marginTop: "16px" }}>Loading aircraft...</p>
              </div>
            </div>
          )}
        </div>

        {/* Controls Grid */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
          gap: "16px",
          marginBottom: "16px"
        }}>
          
          {/* Identity Card */}
          <div style={{ 
            backgroundColor: "#0c0c0c", 
            border: "1px solid #1a1a1a", 
            borderRadius: "8px", 
            padding: "16px" 
          }}>
            <h3 style={{ color: "#60ffd0", marginBottom: "12px", fontSize: "14px" }}>
              AIRLINE IDENTITY
            </h3>
            <div style={{ marginBottom: "12px" }}>
              <label style={{ color: "#888", fontSize: "12px", display: "block", marginBottom: "4px" }}>
                Airline Name
              </label>
              <input
                type="text"
                value={airlineName}
                onChange={(e) => setAirlineName(e.target.value.slice(0, 24))}
                maxLength={24}
                style={{
                  width: "100%",
                  padding: "8px",
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #333",
                  borderRadius: "4px",
                  color: "#fff",
                  fontSize: "14px"
                }}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div>
                <label style={{ color: "#888", fontSize: "12px", display: "block", marginBottom: "4px" }}>
                  Code
                </label>
                <input
                  type="text"
                  value={airlineCode}
                  onChange={(e) => setAirlineCode(e.target.value.toUpperCase().slice(0, 3))}
                  maxLength={3}
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#1a1a1a",
                    border: "1px solid #333",
                    borderRadius: "4px",
                    color: "#fff",
                    fontSize: "14px"
                  }}
                />
              </div>
              <div>
                <label style={{ color: "#888", fontSize: "12px", display: "block", marginBottom: "4px" }}>
                  Callsign
                </label>
                <input
                  type="text"
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value.toUpperCase().slice(0, 10))}
                  maxLength={10}
                  style={{
                    width: "100%",
                    padding: "8px",
                    backgroundColor: "#1a1a1a",
                    border: "1px solid #333",
                    borderRadius: "4px",
                    color: "#fff",
                    fontSize: "14px"
                  }}
                />
              </div>
            </div>
          </div>

          {/* Colors Card */}
          <div style={{ 
            backgroundColor: "#0c0c0c", 
            border: "1px solid #1a1a1a", 
            borderRadius: "8px", 
            padding: "16px" 
          }}>
            <h3 style={{ color: "#60ffd0", marginBottom: "12px", fontSize: "14px" }}>
              🎨 Colors
            </h3>
            
            {/* Primary */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ color: "#60ffd0", fontSize: "11px", display: "block", marginBottom: "6px" }}>
                Primary
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {PRIMARY_COLORS.map((color) => (
                  <div
                    key={color.id}
                    onClick={() => setPrimaryColor(color.hex)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: color.hex,
                      border: primaryColor === color.hex ? "3px solid #60ffd0" : "2px solid #333",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title={color.name}
                  >
                    {primaryColor === color.hex && (
                      <span style={{ fontSize: "12px", fontWeight: "bold", color: getBrightness(color.hex) > 0.5 ? "#000" : "#fff" }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Secondary */}
            <div style={{ marginBottom: "12px" }}>
              <label style={{ color: "#60ffd0", fontSize: "11px", display: "block", marginBottom: "6px" }}>
                Secondary
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {SECONDARY_COLORS.map((color) => (
                  <div
                    key={color.id}
                    onClick={() => setSecondaryColor(color.hex)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: color.hex,
                      border: secondaryColor === color.hex ? "3px solid #60ffd0" : "2px solid #333",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title={color.name}
                  >
                    {secondaryColor === color.hex && (
                      <span style={{ fontSize: "12px", fontWeight: "bold", color: getBrightness(color.hex) > 0.5 ? "#000" : "#fff" }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Accent */}
            <div>
              <label style={{ color: "#60ffd0", fontSize: "11px", display: "block", marginBottom: "6px" }}>
                Accent
              </label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {ACCENT_COLORS.map((color) => (
                  <div
                    key={color.id}
                    onClick={() => setAccentColor(color.hex)}
                    style={{
                      width: "28px",
                      height: "28px",
                      borderRadius: "50%",
                      backgroundColor: color.hex,
                      border: accentColor === color.hex ? "3px solid #60ffd0" : "2px solid #333",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title={color.name}
                  >
                    {accentColor === color.hex && (
                      <span style={{ fontSize: "12px", fontWeight: "bold", color: getBrightness(color.hex) > 0.5 ? "#000" : "#fff" }}>✓</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pattern Card */}
          <div style={{ 
            backgroundColor: "#0c0c0c", 
            border: "1px solid #1a1a1a", 
            borderRadius: "8px", 
            padding: "16px" 
          }}>
            <h3 style={{ color: "#60ffd0", marginBottom: "12px", fontSize: "14px" }}>
              LIVERY PATTERN
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {LIVERY_PATTERNS.map((p) => (
                <div
                  key={p.id}
                  onClick={() => setPattern(p.id)}
                  style={btnStyle(pattern === p.id)}
                >
                  {p.name}
                </div>
              ))}
            </div>
          </div>

          {/* Tail Design Card */}
          <div style={{ 
            backgroundColor: "#0c0c0c", 
            border: "1px solid #1a1a1a", 
            borderRadius: "8px", 
            padding: "16px" 
          }}>
            <h3 style={{ color: "#60ffd0", marginBottom: "12px", fontSize: "14px" }}>
              Tail Design
            </h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {TAIL_DESIGNS.map((td) => (
                <div
                  key={td.id}
                  onClick={() => setTailDesign(td.id)}
                  style={btnStyle(tailDesign === td.id)}
                >
                  {td.name}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Launch Button */}
        <div
          onClick={handleComplete}
          style={{
            width: "100%",
            padding: "20px",
            backgroundColor: "#60ffd0",
            color: "#000",
            fontSize: "20px",
            fontWeight: "bold",
            textAlign: "center",
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            userSelect: "none",
          }}
        >
          Launch {airlineName} →
        </div>
      </div>
    </div>
  );
}